import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  CardComponent,
  cellStyles4,
  Chip,
  Columns,
  Engagement,
  InteractionCard,
  NoDataVisual,
  PatientInfoCard,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { useEffect, useState } from 'react';
import { PatientCasesMessages } from '../../../core-utils/messages/PatientCasesMessages';
import { patientAccessTheme } from '@HomaHealth/ui-component-library';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { PatientCasesTabsProps } from '../PatientCasesTabs';
import Loader from '../Loader';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import {
  CareNavigator,
  caseDetailsLabelMapper,
  formatCareNavigator,
  formatCaseDetails,
  formatInfoCard,
  formatInteractions,
  PatientCurrentHealthState,
} from '../../../core-utils/helper-utils/patientCasesHelper';
import Snackbar from '../../atoms/Snackbar';
import useSnackbar from '../../../core-utils/custom-hooks/Snackbar';
import { Interaction } from '@HomaHealth/ui-component-library/lib/_AIQ-components/molecules/InteractionCard';
import AddInteractionModal from '../../organisms/AddInteractionModal';
import { useStores } from '../../../stores';
import AssignCareNavModal from '../../organisms/AssignCareNavModal';
import { getTextWithToolTip } from '../../../core-utils/helper-utils/riskPatientDetailsHelper';
import {
  getPriorityButton,
  toISOStringFormat,
} from '../../../core-utils/helper-utils/helper';
import moment from 'moment';

const careNavColumns: Columns[] = [
  {
    name: 'Member Name',
    align: 'left',
    cellStyles: cellStyles4,
    width: '130px',
  },
  {
    name: 'Condition',
    align: 'left',
    cellStyles: cellStyles4,
    width: '250px',
  },
  {
    name: 'Priority',
    align: 'left',
    cellStyles: cellStyles4,
    width: '130px',
  },
];

const PatientCaseDetailsTab: React.FC<
  Omit<PatientCasesTabsProps, 'memberId'>
> = ({ caseId, triggerAPI, interactionState }) => {
  const { classes } = makeStyles()({
    tabContainer: {
      margin: '20px 34px',
    },
    cardContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      marginTop: '1rem',
    },
  })();

  const getFormattedCurrentDateTime = () => {
    const now = new Date();
    const offsetDate = new Date(
      now.getTime() - now.getTimezoneOffset() * 60000,
    );
    return offsetDate.toISOString().slice(0, 16);
  };

  const messages = PatientCasesMessages;
  const { snackbarState, openSnackbar, handleCloseSnackBar } = useSnackbar();

  const loginPageStore = useStores().loginPageStore;
  const careNavigatorStore = useStores().careNavigatorStore;

  const [loading, setLoading] = useState<boolean>(false);
  const [caseDetails, setCaseDetails] = useState<PatientCurrentHealthState>({
    conditions: '',
    medications: '',
    procedures: '',
  });
  const [careNavigator, setCareNavigator] = useState<CareNavigator>({
    id: '',
    name: '',
    role: '',
    assignedOn: '',
  });
  const [interactions, setInteractions] =
    interactionState || useState<Interaction[]>([]);
  const [interactionData, setInteractionData] = useState<any>({
    interaction_id: '',
    case_id: '',
    cn_id: '',
    patient_id: '',
    group_id: '',
    interaction_date: getFormattedCurrentDateTime(),
    type: '',
    mode: '',
    referral: '',
    details: '',
  });
  const [isAddInteraction, setIsAddInteraction] = useState<boolean>(false);
  const [isUpdateInteraction, setIsUpdateInteraction] = useState<boolean>(
    false,
  );
  const [referrals, setReferrals] = useState<string[]>([]);

  const [isAssignModal, setIsAssignModal] = useState<boolean>(false);
  const [isReplaceModal, setIsReplaceModal] = useState<boolean>(false);
  const [careNavFieldsData, setCareNavFieldsData] = useState<any>({
    care_navigator_pharmacist: [],
    care_navigator_nurse: [],
    care_navigator_customer_service: [],
  });
  const [assignData, setAssignData] = useState<any>({
    name: '',
    case_id: '',
    priority: '',
    condition: '',
    cn_id: '',
    cn_name: '',
    cn_role: '',
    cn_assign_note: '',
  });

  const [isCaseDetailsEdit, setIsCaseDetailsEdit] = useState<boolean>(false);
  const requiredInteractionDataFields = [
    'case_id',
    'patient_id',
    'group_id',
    'interaction_date',
    'type',
    'mode',
  ];
  const updateInteractionDataFields = [
    'interaction_id',
    'interaction_date',
    'type',
    'mode',
  ];

  const getModalData = () => {
    return [
      [
        assignData.name.length > 12
          ? getTextWithToolTip(assignData.name, 12)
          : assignData.name,
        assignData.condition.length > 28
          ? getTextWithToolTip(assignData.condition, 28)
          : assignData.condition,
        <Grid
          style={{ color: getPriorityButton(assignData.priority).labelColor }}
        >
          {assignData.priority}
        </Grid>,
      ],
    ];
  };

  const handleAssignModal = (action: string) => {
    if (action === 'Replace') {
      setIsReplaceModal(true);
    }
    if (action === 'Assign') {
      setIsAssignModal(true);
    }
    return;
  };

  const handleCaseDetailsEdit = async () => {
    if (isCaseDetailsEdit) {
      try {
        openSnackbar(
          'Current Health State Updated Successfully',
          'success',
          5000,
        );
        const response = await updateCurrentHealthCondtions();
        setCaseDetails(formatCaseDetails([response.data?.data]));
        setIsCaseDetailsEdit(false);
      } catch (error) {
        openSnackbar('Current Health State Save Error', 'error', 5000);
      }
    } else {
      setIsCaseDetailsEdit(true);
    }
  };

  const handleFieldChange = (label: string, value: string) => {
    setCaseDetails((prevDetails) => ({
      ...prevDetails,
      [caseDetailsLabelMapper(label)]: value,
    }));
  };

  const handleAddInteraction = () => {
    setIsAddInteraction(true);
    setInteractionData((prev: any) => ({
      ...prev,
      interaction_id: '',
      interaction_date: '',
      type: '',
      mode: '',
      referral: '',
      details: '',
    }));
  };

  const handleEditInteraction = (id: string) => {
    const interaction = interactions?.find((item: any) => item._id === id);
    if (interaction) {
      setInteractionData((prev: any) => ({
        ...prev,
        interaction_id: interaction._id,
        interaction_date: toISOStringFormat(interaction.date),
        type:
          interaction.interactionType === 'outbound' ? 'Outbound' : 'Inbound',
        mode: interaction.mode,
        referral: interaction.referral,
        details: interaction.touchpointDetails,
      }));
      setIsUpdateInteraction(true);
    }
  };

  const handleupdateInteraction = async () => {
    const formattedDate = moment
      .parseZone(interactionData.interaction_date)
      .format('YYYY-MM-DDTHH:mm:ss[Z]');

    await axios
      .post(
        properties.aiqBaseUrl +
          properties.interactionBaseURL +
          properties.updateInteraction,
        {
          interaction_id: interactionData.interaction_id,
          interaction_date: formattedDate,
          type: interactionData.type,
          mode: interactionData.mode,
          referral: interactionData.referral,
          details: interactionData.details,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          openSnackbar('Interaction updated succesfully', 'success', 6000);
        }
        setIsUpdateInteraction(false);
        apiCalls();
      })
      .catch((error) =>
        openSnackbar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleSave = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.interactionBaseURL +
          properties.addInteraction,
        interactionData,
      )
      .then((response) => {
        if (response.status === 200) {
          openSnackbar('Interaction added succesfully', 'success', 6000);
        }
        setIsAddInteraction(false);
        apiCalls();
      })
      .catch((error) =>
        openSnackbar(error.response?.data?.error, 'error', 6000),
      );
  };

  const getReferrals = async () => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.dynamicFieldsBaseUrl +
        properties.getDynamicFields,
      {
        field: 'referrals',
      },
    );
  };

  const getCaseDetails = async () => {
    const url =
      properties.aiqBaseUrl + properties.casesBaseUrl + properties.caseById;
    const requestBody = {
      case_id: caseId,
    };
    return await axios.post(url, requestBody);
  };

  const getInteractions = async () => {
    const url =
      properties.aiqBaseUrl +
      properties.interactionBaseURL +
      properties.getInteraction;
    const requestBody = {
      case_id: caseId,
    };
    return await axios.post(url, requestBody);
  };

  const updateCurrentHealthCondtions = async () => {
    const url =
      properties.aiqBaseUrl +
      properties.casesBaseUrl +
      properties.updateCaseHealthState;
    const requestBody = {
      case_id: caseId,
      current_health_state: caseDetails,
    };
    return await axios.post(url, requestBody);
  };

  const getCareNavData = async () => {
    return await axios.get(
      properties.userManagementBaseUrl +
        properties.careNavigator +
        properties.getByRole +
        '?groupId=' +
        careNavigatorStore.group_id,
    );
  };

  const handleSaveCareNavigator = async () => {
    await axios
      .post(
        properties.aiqBaseUrl + properties.casesBaseUrl + properties.assignCase,
        {
          case_id: assignData.case_id,
          cn_id: assignData.cn_id,
          cn_name: assignData.cn_name,
          cn_role: assignData.cn_role,
          cn_assign_note: assignData.cn_assign_note,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          openSnackbar('Care navigator added succesfully', 'success', 6000);
        }
        setAssignData((prev: any) => ({
          ...prev,
          cn_id: '',
          cn_name: '',
          cn_role: '',
          cn_assign_note: '',
        }));
        setIsAssignModal(false);
        setIsReplaceModal(false);
        apiCalls();
      })
      .catch((error) =>
        openSnackbar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleAccept = async () => {
    await axios
      .post(
        properties.aiqBaseUrl + properties.casesBaseUrl + properties.acceptCase,
        {
          case_id: assignData.case_id,
        },
      )
      .catch((error) =>
        openSnackbar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleSelfAssignAndAccept = async () => {
    const careNavData = loginPageStore.userResponse;
    if (!careNavData) {
      openSnackbar('Care navigator details not found', 'error', 6000);
      return;
    }

    await axios
      .post(
        properties.aiqBaseUrl + properties.casesBaseUrl + properties.assignCase,
        {
          case_id: assignData.case_id,
          cn_id: careNavData.cnId,
          cn_name: careNavData.firstName + ' ' + careNavData.lastName,
          cn_role: careNavData.role,
          cn_assign_note: assignData.cn_assign_note,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          handleAccept();
          openSnackbar(
            'Care navigator assigned & accepted succesfully',
            'success',
            6000,
          );
        }
        setIsAssignModal(false);
        setIsReplaceModal(false);
        apiCalls();
      })
      .catch((error) =>
        openSnackbar(error.response?.data?.error, 'error', 6000),
      );
  };

  const apiCalls = async () => {
    setLoading(true);
    await Promise.allSettled([
      getCaseDetails(),
      getInteractions(),
      getReferrals(),
      getCareNavData(),
    ]).then((responses) => {
      setLoading(false);

      //getCaseDetails()
      if (responses[0].status === 'fulfilled') {
        const caseData = responses[0].value.data?.data?.[0];
        setCaseDetails(formatCaseDetails(responses[0].value.data?.data));
        setInteractionData((prev: any) => ({
          ...prev,
          case_id: caseData?._id,
          cn_id: caseData?.cn_id,
          group_id: caseData?.group_id,
          patient_id: caseData?.patient_id?._id,
        }));
        setAssignData((prev: any) => ({
          ...prev,
          case_id: caseData?._id,
          group_id: caseData?.group_id,
          patient_id: caseData?.patient_id?._id,
          name: caseData?.patient_id?.name ?? '-',
          priority: caseData?.priority ?? '-',
          condition: caseData?.patient_id?.condition ?? '-',
        }));
        setCareNavigator(formatCareNavigator(responses[0].value.data?.data));
      }
      if (responses[0].status === 'rejected') {
        openSnackbar(responses[0]?.reason, 'error', 5000);
      }

      //getInteractions()
      if (responses[1].status === 'fulfilled') {
        setInteractions(
          formatInteractions(
            responses[1].value.data?.data,
            loginPageStore.isCareNavigator,
          ),
        );
      }
      if (responses[1].status === 'rejected') {
        openSnackbar(responses[1]?.reason, 'error', 5000);
      }

      if (responses[2].status === 'fulfilled') {
        setReferrals(responses[2].value?.data?.data);
      }

      if (responses[3]?.status === 'fulfilled') {
        setCareNavFieldsData(responses[3]?.value?.data);
      }
    });
    setLoading(false);
  };

  useEffect(() => {
    if (triggerAPI) {
      apiCalls();
    }
  }, [triggerAPI]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Grid className={classes.tabContainer}>
        <CardComponent
          title={messages.PATIENT_CASE_DETAILS.TITLE}
          subTitle={messages.PATIENT_CASE_DETAILS.SUBTITLE}
          body={
            <Grid container className={classes.cardContainer}>
              <PatientInfoCard
                title={messages.PATIENT_CASE_DETAILS.HEALTH_STATE.TITLE}
                subtitle={messages.PATIENT_CASE_DETAILS.HEALTH_STATE.SUBTITLE}
                items={formatInfoCard(caseDetails, false)}
                isEdit={isCaseDetailsEdit}
                buttonText={isCaseDetailsEdit ? 'Save' : 'Edit'}
                onButtonClick={handleCaseDetailsEdit}
                width="calc(50% - 0.5rem)"
                handleFieldChange={handleFieldChange}
              />
              <PatientInfoCard
                title={messages.PATIENT_CASE_DETAILS.CARE_NAVIGATOR.TITLE}
                subtitle={messages.PATIENT_CASE_DETAILS.CARE_NAVIGATOR.SUBTITLE}
                items={formatInfoCard(careNavigator, true)}
                isEdit={false}
                buttonText={interactionData?.id ? 'Replace' : 'Assign'}
                onButtonClick={() =>
                  handleAssignModal(interactionData?.id ? 'Replace' : 'Assign')
                }
                width="calc(50% - 0.5rem)"
              />
            </Grid>
          }
          mainGridStyles={{ marginBottom: '1rem' }}
          topRightItem={
            <Chip
              label={
                <Typography
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                  textColor={patientAccessTheme.palette.text.lowemphasis}
                >
                  {messages.PATIENT_CASE_DETAILS.CASE_ID}
                  <b>{caseId}</b>
                </Typography>
              }
              border={`1px solid ${patientAccessTheme.palette.grey[300]}`}
            />
          }
        />

        {interactions.length === 0 ? (
          <CardComponent
            title={messages.PATIENT_CASE_DETAILS.ENGAGEMENT.TITLE}
            subTitle={messages.PATIENT_CASE_DETAILS.ENGAGEMENT.SUBTITLE}
            body={<NoDataVisual height="200px" />}
            topRightItem={
              loginPageStore.isCareNavigator ? (
                <Grid>
                  <Button
                    color="primary"
                    variant="outlined"
                    height="36px"
                    width="200px"
                    label={'Add New Interaction'}
                    onClick={() => handleAddInteraction()}
                    endIcon={<Engagement />}
                  />
                </Grid>
              ) : undefined
            }
          />
        ) : (
          <InteractionCard
            title={messages.PATIENT_CASE_DETAILS.ENGAGEMENT.TITLE}
            subtitle={messages.PATIENT_CASE_DETAILS.ENGAGEMENT.SUBTITLE}
            interactions={interactions}
            handleEditClick={handleEditInteraction}
            topRightItem={
              loginPageStore.isCareNavigator ? (
                <Grid>
                  <Button
                    color="primary"
                    variant="outlined"
                    height="36px"
                    width="200px"
                    label={'Add New Interaction'}
                    onClick={() => handleAddInteraction()}
                    endIcon={<Engagement />}
                  />
                </Grid>
              ) : undefined
            }
            width="100%"
            height="auto"
            titleGridStyles={{
              '& .MuiTypography-body1': {
                '&:first-child': {
                  fontSize: '16px !important',
                },
              },
            }}
          />
        )}
      </Grid>
      {isAddInteraction && (
        <AddInteractionModal
          open={isAddInteraction}
          handleClose={() => setIsAddInteraction(false)}
          handleSave={handleSave}
          isSaveButtonDisabled={
            !requiredInteractionDataFields.every(
              (field) => interactionData[field],
            )
          }
          dataState={[interactionData, setInteractionData]}
          referrals={referrals}
        />
      )}
      {isUpdateInteraction && (
        <AddInteractionModal
          open={isUpdateInteraction}
          handleClose={() => setIsUpdateInteraction(false)}
          handleSave={handleupdateInteraction}
          isSaveButtonDisabled={
            !updateInteractionDataFields.every(
              (field) => interactionData[field],
            )
          }
          dataState={[interactionData, setInteractionData]}
          referrals={referrals}
          update={true}
        />
      )}
      {isAssignModal && (
        <AssignCareNavModal
          open={isAssignModal}
          handleClose={() => setIsAssignModal(false)}
          handleSave={handleSaveCareNavigator}
          isSaveButtonDisabled={!assignData.cn_id}
          columns={careNavColumns}
          rowsData={getModalData()}
          isReplaceModal={false}
          assignCareNavState={[assignData, setAssignData]}
          careNavFieldsData={careNavFieldsData}
          isCareNavigator={loginPageStore.isCareNavigator}
          handleSelfAssign={handleSelfAssignAndAccept}
        />
      )}
      {isReplaceModal && (
        <AssignCareNavModal
          open={isReplaceModal}
          handleClose={() => setIsReplaceModal(false)}
          handleSave={handleSaveCareNavigator}
          isSaveButtonDisabled={!assignData.cn_id}
          columns={careNavColumns}
          rowsData={getModalData()}
          isReplaceModal={true}
          assignCareNavState={[assignData, setAssignData]}
          careNavFieldsData={careNavFieldsData}
          isCareNavigator={loginPageStore.isCareNavigator}
          handleSelfAssign={handleSelfAssignAndAccept}
        />
      )}
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
};

export default PatientCaseDetailsTab;
